import { gql, useQuery } from "@apollo/client";
import { useMemo } from "react";
import { SymbolType, isSvgType } from "../../../../types/floorplannerTypes";
import {
  CatalogImageOwner,
  useFloorplannerProduct3DAssets,
} from "../../catalog/catalogQueries";
import { getResizedCatalogImageUrl } from "../../materials/textureCache";
import { normalizeCatalogProductDimensions } from "../../products/productDimensionUtils";
import {
  hydrateProduct3DReference,
  selectHydratableProduct3DAsset,
} from "../../products/product3DAssetHydration";
import {
  Product3DAssetReference,
  Product3DReference,
} from "../../products/product3DTypes";
import {
  PrimitiveSymbol3DModelKey,
  getPrimitiveSymbol3DDefaults,
  getPrimitiveSymbol3DLibraryKeyForSymbol,
  getPrimitiveSymbol3DModelKey,
  getPrimitiveSymbol3DModelKeyForSymbol,
} from "../registry/primitiveSymbol3DConfig";

type SymbolUserModel3DReference = NonNullable<
  NonNullable<SymbolType["threeD"]>["userModel"]
>;

type FloorplannerPublicModel3D = {
  id: string;
  name?: string | null;
  status?: string | null;
  optimizedUrl?: string | null;
  thumbnailUrl?: string | null;
  dimensions?: SymbolUserModel3DReference["dimensions"] | null;
  bounds?: SymbolUserModel3DReference["bounds"] | null;
  sha256?: string | null;
  pipelineVersion?: string | null;
};

export type FloorplannerSymbol3DDefault = {
  id: string;
  symbolKey: string;
  standardModelKey?: string | null;
  productId?: string | null;
  variantId?: string | null;
  publicModelId?: string | null;
  modelPitchOffsetDeg?: number | null;
  modelRollOffsetDeg?: number | null;
  modelYawOffsetDeg?: number | null;
  enabled?: boolean | null;
  product?: CatalogImageOwner | null;
  publicModel?: FloorplannerPublicModel3D | null;
};

type FloorplannerSymbol3DDefaultsData = {
  floorplannerSymbol3DDefaults?: FloorplannerSymbol3DDefault[] | null;
};

type FloorplannerSymbol3DDefaultsVariables = {
  symbolKeys: string[];
};

export const FLOORPLANNER_SYMBOL_3D_DEFAULTS_QUERY = gql`
  query FloorplannerSymbol3DDefaults($symbolKeys: [String!]) {
    floorplannerSymbol3DDefaults(symbolKeys: $symbolKeys) {
      id
      symbolKey
      standardModelKey
      productId
      variantId
      publicModelId
      modelPitchOffsetDeg
      modelRollOffsetDeg
      modelYawOffsetDeg
      enabled
      publicModel {
        id
        name
        status
        optimizedUrl
        thumbnailUrl
        dimensions {
          width
          height
          depth
        }
        bounds {
          min
          max
          center
          size {
            width
            height
            depth
          }
        }
        sha256
        pipelineVersion
      }
      product {
        id
        name
        brand
        description
        width
        height
        depth
        images
        supplier {
          id
          name
        }
        categoriesMeta {
          id
          name
          parent {
            name
          }
        }
        features3
        breakColors
      }
    }
  }
`;

export function useFloorplannerSymbol3DDefaults(
  symbolKeys: string[],
  enabled = true,
) {
  const distinctSymbolKeys = useMemo(
    () => Array.from(new Set(symbolKeys.filter(Boolean))).sort(),
    [symbolKeys],
  );
  const defaultsQuery = useQuery<
    FloorplannerSymbol3DDefaultsData,
    FloorplannerSymbol3DDefaultsVariables
  >(FLOORPLANNER_SYMBOL_3D_DEFAULTS_QUERY, {
    variables: { symbolKeys: distinctSymbolKeys },
    fetchPolicy: "cache-and-network",
    skip: !enabled || distinctSymbolKeys.length === 0,
  });
  // Apollo 3 clears `data` when a query becomes skipped. The 3D scene remains
  // mounted briefly while its exit transition restores the 2D camera, so keep
  // the last resolved defaults during that teardown instead of flashing the
  // symbol's persisted primitive metadata.
  const defaultsData = defaultsQuery.data ?? defaultsQuery.previousData;
  const defaults = useMemo(
    () => defaultsData?.floorplannerSymbol3DDefaults ?? [],
    [defaultsData],
  );
  const defaultProductIds = useMemo(
    () =>
      Array.from(
        new Set(
          defaults
            .map((symbolDefault) => symbolDefault.productId)
            .filter((id): id is string => Boolean(id)),
        ),
      ).sort(),
    [defaults],
  );
  const assetsQuery = useFloorplannerProduct3DAssets(
    defaultProductIds,
    !enabled || defaultProductIds.length === 0,
  );

  const defaultsByKey = useMemo(() => {
    const map = new Map<string, FloorplannerSymbol3DDefault>();
    defaults.forEach((symbolDefault) => {
      if (symbolDefault.enabled === false) return;
      map.set(symbolDefault.symbolKey, symbolDefault);
    });
    return map;
  }, [defaults]);

  const defaultProductAssetsByProductId = useMemo(() => {
    const map = new Map<string, Product3DAssetReference[]>();
    const assetsData = assetsQuery.data ?? assetsQuery.previousData;
    const assets = (assetsData?.product3DAssets ??
      []) as Product3DAssetReference[];
    assets.forEach((asset) => {
      if (!asset.productId) return;
      const productAssets = map.get(asset.productId) ?? [];
      productAssets.push(asset);
      map.set(asset.productId, productAssets);
    });
    return map;
  }, [assetsQuery.data, assetsQuery.previousData]);

  const signature = useMemo(
    () =>
      defaults
        .map((symbolDefault) =>
          [
            symbolDefault.symbolKey,
            symbolDefault.standardModelKey ?? "",
            symbolDefault.productId ?? "",
            symbolDefault.variantId ?? "",
            symbolDefault.publicModelId ?? "",
            symbolDefault.publicModel?.status ?? "",
            symbolDefault.publicModel?.optimizedUrl ?? "",
            symbolDefault.publicModel?.sha256 ?? "",
            symbolDefault.publicModel?.pipelineVersion ?? "",
            symbolDefault.modelPitchOffsetDeg ?? "",
            symbolDefault.modelRollOffsetDeg ?? "",
            symbolDefault.modelYawOffsetDeg ?? "",
            symbolDefault.enabled === false ? "disabled" : "enabled",
          ].join(":"),
        )
        .join("|") +
      "|" +
      Array.from(defaultProductAssetsByProductId.entries())
        .flatMap(([productId, assets]) =>
          assets.map((asset) =>
            [
              productId,
              asset.id,
              asset.variantId ?? "",
              asset.glbUrl ?? "",
              asset.quality?.sha256 ?? "",
            ].join(":"),
          ),
        )
        .sort()
        .join("|"),
    [defaultProductAssetsByProductId, defaults],
  );

  return {
    defaultsByKey,
    defaultProductAssetsByProductId,
    loading: defaultsQuery.loading || assetsQuery.loading,
    error: defaultsQuery.error ?? assetsQuery.error,
    signature,
  };
}

export function useEffectiveFloorplannerSymbol3DDefaults<T extends SymbolType>(
  symbols: T[],
  enabled = true,
): T[] {
  const symbolKeys = useMemo(
    () =>
      Array.from(
        new Set(
          symbols
            .map(getFloorplannerSymbol3DDefaultKey)
            .filter((key): key is string => Boolean(key)),
        ),
      ).sort(),
    [symbols],
  );
  const { defaultsByKey, defaultProductAssetsByProductId, signature } =
    useFloorplannerSymbol3DDefaults(symbolKeys, enabled && symbols.length > 0);

  return useMemo(
    () =>
      enabled
        ? (applyFloorplannerSymbol3DDefaults(
            symbols,
            defaultsByKey,
            defaultProductAssetsByProductId,
          ) as T[])
        : symbols,
    [
      defaultProductAssetsByProductId,
      defaultsByKey,
      enabled,
      signature,
      symbols,
    ],
  );
}

export function getFloorplannerSymbol3DDefaultKey(
  symbol: SymbolType,
): string | undefined {
  const key = symbol.libraryKey?.trim();
  return key || getPrimitiveSymbol3DLibraryKeyForSymbol(symbol);
}

export function applyFloorplannerSymbol3DDefaults(
  symbols: SymbolType[],
  defaultsByKey: Map<string, FloorplannerSymbol3DDefault>,
  productAssetsByProductId: Map<string, Product3DAssetReference[]> = new Map(),
): SymbolType[] {
  if (defaultsByKey.size === 0) return symbols;
  return symbols.map((symbol) =>
    applyFloorplannerSymbol3DDefault(
      symbol,
      defaultsByKey.get(getFloorplannerSymbol3DDefaultKey(symbol) ?? ""),
      productAssetsByProductId,
    ),
  );
}

/**
 * Symbols that may receive a server-managed 3D source must stay hidden until
 * that source is resolved. Rendering their local primitive in the meantime
 * causes a visible primitive-to-uploaded-model flash when entering 3D.
 */
export function getSymbolsReadyForFloorplanner3DDefaults<T extends SymbolType>(
  symbols: T[],
  loading: boolean,
): T[] {
  if (!loading) return symbols;
  return symbols.filter(
    (symbol) => !shouldApplyFloorplannerSymbol3DDefault(symbol),
  );
}

export function applyFloorplannerSymbol3DDefault(
  symbol: SymbolType,
  symbolDefault: FloorplannerSymbol3DDefault | undefined,
  productAssetsByProductId: Map<string, Product3DAssetReference[]> = new Map(),
): SymbolType {
  if (!symbolDefault || symbolDefault.enabled === false) return symbol;
  if (!shouldApplyFloorplannerSymbol3DDefault(symbol)) return symbol;

  const standardModelKey = getPrimitiveSymbol3DModelKey(
    symbolDefault.standardModelKey ?? undefined,
  );
  const defaultProduct = createDefaultProductReference(
    symbol,
    symbolDefault,
    standardModelKey,
    productAssetsByProductId,
  );
  const defaultUserModel = createDefaultUserModelReference(
    symbol,
    symbolDefault,
  );

  if (!standardModelKey && !defaultProduct && !defaultUserModel) return symbol;

  const standardDefaults = getPrimitiveSymbol3DDefaults(standardModelKey);
  const defaultHeight =
    symbol.threeD?.height ??
    defaultUserModel?.dimensions?.height ??
    standardDefaults?.height;
  const modelPitchOffset = degreesToRadians(symbolDefault.modelPitchOffsetDeg);
  const modelRollOffset = degreesToRadians(symbolDefault.modelRollOffsetDeg);
  const modelYawOffset = degreesToRadians(symbolDefault.modelYawOffsetDeg);
  return {
    ...symbol,
    threeD: {
      ...symbol.threeD,
      // Applies to product and standard assets; uploaded models carry the same
      // correction on their user-model metadata.
      ...(modelPitchOffset != null ? { modelPitchOffset } : undefined),
      ...(modelRollOffset != null ? { modelRollOffset } : undefined),
      ...(modelYawOffset != null ? { modelYawOffset } : undefined),
      ...(standardModelKey
        ? {
            modelKey: standardModelKey,
            height: defaultHeight,
            baseElevation:
              symbol.threeD?.baseElevation ?? standardDefaults?.baseElevation,
            collision: symbol.threeD?.collision ?? standardDefaults?.collision,
            dimensions: {
              ...standardDefaults?.dimensions,
              ...symbol.threeD?.dimensions,
              ...(defaultHeight != null
                ? { height: defaultHeight }
                : undefined),
            },
          }
        : defaultUserModel
          ? {
              height: defaultHeight,
              dimensions: {
                ...symbol.threeD?.dimensions,
                ...(defaultHeight != null
                  ? { height: defaultHeight }
                  : undefined),
              },
            }
          : undefined),
      // A floorplanner default is one visual source. Clear a persisted older
      // default when the current configuration switches source type.
      product: defaultProduct,
      userModel: defaultUserModel,
    },
  };
}

export function shouldApplyFloorplannerSymbol3DDefault(
  symbol: SymbolType,
): boolean {
  if (symbol.threeD?.sourceMode === "primitiveOnly") return false;
  if (symbol.threeD?.sourceMode === "custom") return false;
  return Boolean(getFloorplannerSymbol3DDefaultKey(symbol));
}

function createDefaultUserModelReference(
  symbol: SymbolType,
  symbolDefault: FloorplannerSymbol3DDefault,
): SymbolUserModel3DReference | undefined {
  const model = symbolDefault.publicModel;
  if (!model || model.status !== "ready" || !model.optimizedUrl) {
    return undefined;
  }
  // Public models are fitted to the placed library symbol's footprint. Keep
  // that fitted size as the model's target dimensions as well, otherwise a
  // pitch/roll update makes the asset resolver switch from svgLength/svgHeight
  // to the public model's source dimensions and the symbol jumps in size.
  const targetDimensions = {
    ...model.dimensions,
    ...(isSvgType(symbol) && positiveDimension(symbol.svgLength) != null
      ? { width: symbol.svgLength }
      : undefined),
    ...(isSvgType(symbol) && positiveDimension(symbol.svgHeight) != null
      ? { depth: symbol.svgHeight }
      : undefined),
  };
  return {
    modelId: model.id,
    url: model.optimizedUrl,
    version: model.pipelineVersion ?? undefined,
    status: "ready",
    name: model.name ?? undefined,
    thumbnailUrl: model.thumbnailUrl ?? undefined,
    dimensions: Object.values(targetDimensions).some((value) => value != null)
      ? targetDimensions
      : undefined,
    bounds: model.bounds ?? undefined,
    sha256: model.sha256 ?? undefined,
    pitchOffset: degreesToRadians(symbolDefault.modelPitchOffsetDeg),
    rollOffset: degreesToRadians(symbolDefault.modelRollOffsetDeg),
    yawOffset: degreesToRadians(symbolDefault.modelYawOffsetDeg),
  };
}

function positiveDimension(value: number | undefined): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : undefined;
}

function degreesToRadians(
  value: number | null | undefined,
): number | undefined {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return undefined;
  }
  return (value * Math.PI) / 180;
}

function createDefaultProductReference(
  symbol: SymbolType,
  symbolDefault: FloorplannerSymbol3DDefault,
  standardModelKey: PrimitiveSymbol3DModelKey | undefined,
  productAssetsByProductId: Map<string, Product3DAssetReference[]>,
): Product3DReference | undefined {
  if (!symbolDefault.productId) return undefined;
  const product = symbolDefault.product;
  const imageUrl = product?.images?.[0]
    ? getResizedCatalogImageUrl(product.images[0])
    : undefined;
  const displayName = product?.name ?? undefined;
  const normalizedDimensions = product
    ? normalizeCatalogProductDimensions(product)
    : undefined;
  const proxyModelKey =
    standardModelKey ?? getPrimitiveSymbol3DModelKeyForSymbol(symbol);
  const baseProductReference: Product3DReference = {
    productId: symbolDefault.productId,
    variantId: symbolDefault.variantId ?? undefined,
    renderMode: "primitiveProxy",
    selectedImageUrl: imageUrl,
    name: displayName,
    brand: product?.brand ?? undefined,
    supplierName: product?.supplier?.name ?? undefined,
    dimensions: normalizedDimensions?.dimensions,
    dimensionConfidence: normalizedDimensions?.confidence,
    dimensionUnit: normalizedDimensions?.unit,
    dimensionSource: normalizedDimensions?.source,
    proxyModelKey,
    fallbackReason: "Using floorplanner 3D default product proxy.",
  };
  const asset = selectHydratableProduct3DAsset(
    baseProductReference,
    productAssetsByProductId.get(symbolDefault.productId) ?? [],
  );
  return asset
    ? hydrateProduct3DReference(
        {
          ...baseProductReference,
          dimensions: undefined,
          dimensionConfidence: undefined,
          dimensionUnit: undefined,
          dimensionSource: undefined,
        },
        asset,
      )
    : baseProductReference;
}
